import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import type { CompatReportDocument } from '../types';
import { cn } from '../../../../../lib/utils';

interface ShortlistViewProps {
  result: CompatReportDocument;
}

export const ShortlistView: React.FC<ShortlistViewProps> = ({ result }) => {
  const sl = result.shortlist;
  const [copied, setCopied] = useState(false);

  if (!sl) {
    return (
      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
        <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
          Shortlist
        </p>
        <p className="text-[15px] text-text-grey">
          This run did not ask for a shortlist. Choose{' '}
          <span className="font-semibold text-blueprint-blue">Must deliver N</span> or{' '}
          <span className="font-semibold text-blueprint-blue">Best N picks</span> on Set up and
          this tab fills in.
        </p>
      </div>
    );
  }

  const pipeCount = sl.pipeIds?.length ?? 0;
  const verified = sl.verified;
  const accent = sl.deliversAsk ? 'bg-galvanizing-green' : 'bg-[#E6A100]';

  const headline = (() => {
    if (!verified) return null;
    if (sl.pipesToDeliver != null && sl.deliversAsk) {
      return {
        big: String(sl.pipesToDeliver),
        unit: `pipes covered, by the ${pipeCount} below (typically ${Math.round(verified.pipesPlacedAvg)} weld in)`,
      };
    }
    const asked =
      sl.pipesToDeliver != null ? ` (asked: ${sl.pipesToDeliver})` : '';
    return {
      big: String(Math.round(verified.pipesPlacedAvg)),
      unit: `of ${pipeCount} shortlisted pipes weld in${asked}`,
    };
  })();

  const handleCopy = async () => {
    if (!pipeCount) return;
    try {
      await navigator.clipboard.writeText(sl.pipeIds.join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="space-y-4">
      <div
        className={cn(
          'rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden'
        )}
      >
        <div className={cn('absolute top-0 left-0 right-0 h-[3px]', accent)} />
        <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
          The ask, verified with its own run
        </p>
        {headline && (
          <div className="font-heading font-bold text-blueprint-blue text-[clamp(2.5rem,8vw,4rem)] leading-none">
            {headline.big}
            <span className="text-[16px] sm:text-[18px] text-heritage-grey font-semibold ml-1.5 align-middle">
              {headline.unit}
            </span>
          </div>
        )}
        {sl.statement && (
          <p className="mt-3.5 text-[15px] text-text-grey">{sl.statement}</p>
        )}
        {verified && (
          <p className="mt-3 text-[12.5px] text-heritage-grey">
            Verified with its own run of {verified.orders.toLocaleString()} builds:{' '}
            {Math.round((verified.confidence || 0.95) * 100)}% sure, give or take{' '}
            {verified.giveOrTakePipes} pipe{verified.giveOrTakePipes === 1 ? '' : 's'}.
          </p>
        )}
      </div>

      {pipeCount > 0 && (
        <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
          <div className="flex items-start justify-between gap-3 mb-3">
            <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey">
              The {pipeCount} pipe numbers, most dependable first
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 shrink-0 border-[#E2E5EA]"
              onClick={() => void handleCopy()}
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-galvanizing-green" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              <span className="ml-1.5">{copied ? 'Copied' : 'Copy list'}</span>
            </Button>
          </div>
          <div className="grid grid-cols-6 gap-x-4 gap-y-1 text-[12.5px] text-blueprint-blue">
            {sl.pipeIds.map((pipeId, i) => (
              <div key={`${pipeId}-${i}`} className="min-w-0 truncate py-0.5">
                <span className="text-[11px] text-heritage-grey">{i + 1}.</span>{' '}
                <span className="font-semibold">{pipeId}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
